// frontend/src/modules/invoicing/pages/ClientEditorPage.tsx
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { VISUAL } from '../../../design-system/visual';
import { clientsApi } from '../api';
import type { Client, CreateClientInput } from '../types';

const EMPTY: CreateClientInput = {
  name: '', email: '', phone: '', address: '', billing_address: '', tax_id: '', notes: '', currency: null,
};

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'PKR', 'AED', 'SAR', 'JPY', 'CHF', 'SEK', 'NOK', 'NZD', 'SGD', 'ZAR'];

export function ClientEditorPage() {
  const { id } = useParams<{ id: string }>();
  const mode = id ? 'edit' : 'new';
  const navigate = useNavigate();
  const { tenant } = useBranding();
  const tenantCurrency = (tenant as any)?.currency as string | undefined;

  const { data, loading } = useApi<{ client: Client }>(mode === 'edit' && id ? `/api/invoicing/clients/${id}` : null);
  const [form, setForm] = React.useState<CreateClientInput>(EMPTY);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (data?.client) {
      setForm({
        name: data.client.name,
        email: data.client.email ?? '',
        phone: data.client.phone ?? '',
        address: data.client.address ?? '',
        billing_address: data.client.billing_address ?? '',
        tax_id: data.client.tax_id ?? '',
        notes: data.client.notes ?? '',
        currency: data.client.currency ?? null,
        // Preserve the stored timestamp so a save that doesn't touch consent
        // cannot silently re-stamp (or revoke) it.
        marketing_consent_at: data.client.marketing_consent_at ?? null,
        marketing_consent_source: data.client.marketing_consent_source ?? null,
      });
    }
  }, [data]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (mode === 'edit' && id) {
        await clientsApi.update(id, form);
        toast.success('Client updated');
        navigate(`/dashboard/clients/${id}`);
      } else {
        const created = await clientsApi.create(form);
        toast.success('Client created');
        navigate(`/dashboard/clients/${created.id}`);
      }
    } catch (err: any) {
      toast.error(`Save failed`, { description: err.message ?? String(err) });
    } finally {
      setSaving(false);
    }
  }

  if (mode === 'edit' && loading) return <LoadingState variant="page" />;

  const cancelHref = mode === 'edit' && id ? `/dashboard/clients/${id}` : '/dashboard/clients';

  return (
    <form onSubmit={save}>
      <PageHeader
        eyebrow={<><Link to="/dashboard/clients" className="hover:text-foreground transition-colors">Clients</Link> / {mode === 'edit' ? data?.client?.name : 'New'}</>}
        title={mode === 'edit' ? `Edit ${data?.client?.name ?? 'client'}` : 'New client'}
        description={mode === 'edit' ? 'Update contact info, billing details, and notes.' : 'Add a client once, reuse on every invoice.'}
        actions={
          <div className="flex items-center gap-2">
            <Link to={cancelHref}>
              <Button variant="outline" type="button" className="rounded-full h-10 px-4 text-[13px] font-semibold">Cancel</Button>
            </Link>
            <Button type="submit" disabled={saving} className="rounded-full h-10 px-5 text-[13px] font-semibold">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl space-y-6">
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Identity</h3>
          <div className="grid gap-5">
            <Field label="Name" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Acme Co." />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Email">
                <Input type="email" value={form.email ?? ''} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="billing@acme.co" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 (415) 555-0118" />
              </Field>
            </div>
          </div>
        </section>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Address</h3>
          <div className="grid gap-5">
            <Field label="Address">
              <Textarea rows={2} value={form.address ?? ''} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, city, postal code" />
            </Field>
            <Field label="Billing address" description="Used on invoices. Leave blank to reuse the address above.">
              <Textarea rows={2} value={form.billing_address ?? ''} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} />
            </Field>
            <Field label="Tax ID">
              <Input value={form.tax_id ?? ''} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} placeholder="VAT, EIN, GSTIN…" />
            </Field>
            <Field label="Billing currency" description={`New invoices for this client are issued in this currency. No conversion — amounts are entered as-is.${tenantCurrency ? ` Workspace default: ${tenantCurrency}.` : ''}`}>
              <Select
                value={form.currency ?? 'default'}
                onValueChange={(v) => setForm({ ...form, currency: v === 'default' ? null : v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Workspace default{tenantCurrency ? ` (${tenantCurrency})` : ''}</SelectItem>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            {/* Marketing consent (078). A client with no consent on file is
                excluded from every campaign — GDPR requires the sender to be
                able to DEMONSTRATE consent, so it is never assumed. Without
                this control the campaigns module cannot reach anyone. */}
            <Field
              label="Marketing consent"
              description="Tick only if this contact agreed to receive marketing email from you. Contacts without consent are skipped by every campaign. Invoices, receipts and reminders are unaffected — those are transactional and always send."
            >
              <label className="flex items-start gap-3 rounded-2xl bg-white/60 dark:bg-white/[0.04] ring-1 ring-ink/[0.08] dark:ring-white/10 p-3.5 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-input accent-coral"
                  checked={!!form.marketing_consent_at}
                  onChange={(e) => setForm({
                    ...form,
                    marketing_consent_at: e.target.checked ? true : null,
                    marketing_consent_source: e.target.checked ? (form.marketing_consent_source || '') : null,
                  })}
                />
                <span className="text-[13.5px] text-ink dark:text-white">
                  This contact has agreed to receive marketing email
                  {data?.client?.marketing_consent_at && (
                    <span className="block text-[12px] text-muted-foreground mt-0.5">
                      Recorded {new Date(data.client.marketing_consent_at).toLocaleDateString()}
                    </span>
                  )}
                </span>
              </label>
            </Field>
            {!!form.marketing_consent_at && (
              <Field
                label="How was consent given?"
                description="Keep a short record of where the agreement came from — this is what you would show if consent is ever questioned."
              >
                <Input
                  maxLength={200}
                  value={form.marketing_consent_source ?? ''}
                  onChange={(e) => setForm({ ...form, marketing_consent_source: e.target.value })}
                  placeholder="Signup form, signed contract, imported list…"
                />
              </Field>
            )}
          </div>
        </section>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Notes</h3>
          <Field label="Internal notes" description="Only visible to your team. Not shown on invoices.">
            <Textarea rows={4} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Pay slowly, billing contact is finance@acme.co, ask before invoicing &gt; $5k…" />
          </Field>
        </section>

        <div className="flex items-center justify-between pt-2">
          <Link to={cancelHref} className="text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors">
            Back without saving
          </Link>
          <Button type="submit" disabled={saving} className="rounded-full h-11 px-6 text-[14px] font-semibold">
            {saving ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Create client')}
          </Button>
        </div>
      </div>
    </form>
  );
}

function Field({ label, description, required, children }: {
  label: string; description?: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
      {children}
      {description && <p className="text-[12px] text-muted-foreground">{description}</p>}
    </div>
  );
}
